import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import axios from 'axios';
import pako from 'pako';
import { message } from 'antd';
import ENVIRONMENT from '../../../../environments';
import { CREATE_COLLABORATE_ENTITY, REVIEW_IMAGE_CONTROLLER } from '../../../../environments/env';
import CustomerMainLayout from '../CustomerMainLayout';
import AIEditorModalViewer from '../AIEditorModalViewer/AIEditorModalViewer';
import CompareSiloView from '../AIEditorModalViewer/CompareSiloView';
import BulkTaskContext from '../../ContextFiles/BulkTaskContext';

const toBase64Url = (uint8) => {
  let binary = '';
  for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const compressState = (stateObj) => {
  const json = JSON.stringify(stateObj || {});
  const deflated = pako.deflate(json);
  return toBase64Url(deflated);
};

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const MigratedReviewImages = ({ location }) => {
  const iframeRef = React.useRef(null);
  const { addTask } = useContext(BulkTaskContext);
  const migratedPortalBaseUrl = (ENVIRONMENT.MIGRATED_PORTAL_URL || process.env.REACT_APP_MIGRATED_PORTAL_URL || 'http://localhost:5173').replace(/\/$/, '');
  const iframeSrc = `${migratedPortalBaseUrl}/review-images${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch (error) {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  // --- Modal state ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [compareSiloModalOpen, setCompareSiloModalOpen] = useState(false);
  const [imageDisplayLoader, setImageDisplayLoader] = useState(false);

  // Store filteredImages from iframe for navigation
  const [iframeFilteredImages, setIframeFilteredImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // --- Helpers ---
  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  // --- Modal callbacks ---
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedImageData(null);
    setCompareSiloModalOpen(false);
  };

  const closeCompareModal = () => {
    setCompareSiloModalOpen(false);
    setSelectedImageData(null);
  };

  const buildImageDataFromReviewImage = (image) => {
    const isVideo = image.image_type === 'ai_video';
    const imageUrl = ENVIRONMENT.getBaseURL() + image.image_uri;
    const videoUrl = image.video_url ? ENVIRONMENT.getBaseURL() + image.video_url : '';

    return {
      ...image,
      selectedImageUrl: (isVideo && image.video_url) ? videoUrl : imageUrl,
      render_id: image.id,
      isFavourite: false,
      favouriteId: null,
      display_name: image.title,
      image_prompt: image.prompt_text || '',
      prompt_text: image.prompt_text,
      flux_prompt: image.flux_prompt,
      render_submitted_on: image.submitted_on,
      customer_username: image.created_by,
      image_width: image.width,
      image_height: image.height,
      input_image_uri: image.input_image_uri,
      processed_silo: image.processed_silo,
      renders: iframeFilteredImages.map(img => ({
        uri: img.image_uri,
        render_id: img.id,
        asset_type: img.image_type || 'ai_image',
      })),
    };
  };

  const goToNextImage = () => {
    if (currentImageIndex < iframeFilteredImages.length - 1) {
      const nextIndex = currentImageIndex + 1;
      setCurrentImageIndex(nextIndex);
      const nextImage = iframeFilteredImages[nextIndex];
      setSelectedImageData(buildImageDataFromReviewImage(nextImage));
    }
  };

  const goToPreviousImage = () => {
    if (currentImageIndex > 0) {
      const prevIndex = currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      const prevImage = iframeFilteredImages[prevIndex];
      setSelectedImageData(buildImageDataFromReviewImage(prevImage));
    }
  };

  const markAiImageFavorite = () => { /* no-op for review */ };
  const unmarkAiImageFavorite = () => { /* no-op for review */ };
  const renderDelete = () => null;

  const updatedRenderNameInImagesList = (imageData, updatedDisplayName) => {
    if (selectedImageData && (selectedImageData.render_id === imageData.render_id || selectedImageData.id === imageData.id)) {
      setSelectedImageData((prev) => ({
        ...prev,
        title: updatedDisplayName,
        display_name: updatedDisplayName,
      }));
    }
  };

  const handleSubmitForReviewImageLibrary = () => {
    // Already in review — no-op
  };

  const handleCollaborateAiImage = async (imageData) => {
    const image = imageData || selectedImageData;
    if (!image) return;

    const promptText = image.flux_prompt || image.prompt_text;
    const state = {
      img: image.input_image_uri || '',
      imgInspection: image.inspiration_image_uri || '',
      inspiration_id: image.inspiration_id ?? null,
      prompt: promptText || '',
      props: image.props || [],
    };

    const collaborateParams = new URLSearchParams();
    collaborateParams.set('ai_image', 'true');
    collaborateParams.set('productId', image.product_id || '');
    if (image.job_id) collaborateParams.set('job_id', String(image.job_id));
    if (image.inspiration_id != null) collaborateParams.set('inspiration_id', String(image.inspiration_id));
    collaborateParams.set('state', compressState(state));

    if (image.collab_id) {
      window.location.href = `/collaborate-tool/${image.collab_id}?${collaborateParams.toString()}`;
    } else {
      const userName = localStorage.getItem('username');
      const payload = {
        image_id: image.id || image.render_id,
        display_name: image.title || image.display_name || 'Untitled',
        camera_name: image.image_uri,
        thumbnail: image.image_uri.replace(/(\.[^.]+)$/, '_512$1'),
        created_by: userName,
        last_modified_by: userName,
        render_path: image.image_uri,
        state: {},
      };
      const res = await axios.post(CREATE_COLLABORATE_ENTITY, payload);
      window.location.href = `/collaborate-tool/${res.data.body.collaboration_entity_id}?${collaborateParams.toString()}`;
    }
  };

  const refetch = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    console.log("Legacy project call1")
    if (!iframeWindow) return;
    console.log("Legacy project call2")
    iframeWindow.postMessage({ type: 'REVIEW_REFETCH' }, targetOrigin);
  }, [targetOrigin]);

  const routeToImageLibraryVideoTab = () => {
    window.location.href = '/images?type=ai_video';
  };

  const canEditStatus = ['admin', 'reviewer'].includes(localStorage.getItem('user_role'));

  const handleAcceptFromModal = async (imageData) => {
    const isVideo = imageData?.image_type === 'ai_video';
    const payload = {
      action: 'update_status',
      status: 'approved',
      done_by: localStorage.getItem('username') || 'unknown_user',
      ...(imageData?.collab_id ? { collab_id: imageData.collab_id } : {}),
      ...(isVideo ? { video_id: imageData.render_id || imageData.id } : { image_id: imageData.render_id || imageData.id }),
    };
    message.loading('Approving image...', 1.5);
    try {
      await axios.post(REVIEW_IMAGE_CONTROLLER, payload);
      message.success('Image approved');
      refetch();
    } catch {
      message.error('Failed to approve image');
    }
    handleModalClose();
  };

  const handleRejectFromModal = async (imageData) => {
    const isVideo = imageData?.image_type === 'ai_video';
    if (!isVideo) {
      message.info('Opening feedback tool...');
      handleModalClose();
      handleCollaborateAiImage(imageData);
      return;
    }
    const payload = {
      action: 'update_status',
      status: 'rejected',
      done_by: localStorage.getItem('username') || 'unknown_user',
      ...(imageData?.collab_id ? { collab_id: imageData.collab_id } : {}),
      video_id: imageData.render_id || imageData.id,
    };
    message.loading('Rejecting image...', 1.5);
    try {
      await axios.post(REVIEW_IMAGE_CONTROLLER, payload);
      message.success('Image rejected');
      refetch();
    } catch {
      message.error('Failed to reject image');
    }
    handleModalClose();
  };

  // --- postMessage listener ---
  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type, payload } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
      } else if (type === 'ALL3D_NAVIGATE') {
        window.location.href = event.data.url || '';
      } else if (type === 'REVIEW_OPEN_PREVIEW') {
        const { imageData, filteredImages, currentIndex } = payload || {};
        if (filteredImages) {
          setIframeFilteredImages(filteredImages);
        }
        setCurrentImageIndex(currentIndex || 0);
        setSelectedImageData(imageData);
        setIsModalOpen(true);
      } else if (type === 'REVIEW_COLLABORATE') {
        const { collabId, collaborateParams } = payload || {};
        window.location.href = `/collaborate-tool/${collabId}?${collaborateParams}`;
      } else if (type === 'REVIEW_TAB_CHANGE') {
        const { tab } = payload || {};
        if (tab) {
          const params = new URLSearchParams(window.location.search);
          params.set('tab', tab);
          window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
        }
      } else if (type === 'REVIEW_BULK_DOWNLOAD') {
        const { imageIds, format } = payload || {};
        if (!Array.isArray(imageIds) || imageIds.length === 0) return;

        const companyId = localStorage.getItem('company_id');
        const username = localStorage.getItem('username');
        const imageCount = imageIds.length;

        axios.post(ENVIRONMENT.AI_MODEL_HELPER, {
          action: 'trigger_bulk_action_download',
          image_ids: imageIds,
          format: format || 'jpg',
          company_id: Number(companyId),
          created_by: username,
        }).then((res) => {
          const bulkActionId = res.data?.bulk_action_id;
          if (!bulkActionId) {
            message.error('Failed to initiate bulk download — no action ID returned.');
            return;
          }
          addTask({
            title: 'Preparing download',
            label: `Zipping ${imageCount} file${imageCount !== 1 ? 's' : ''}`,
            imageCount,
            bulkActionId,
          });
        }).catch((error) => {
          console.error('Bulk download error:', error);
          message.error('Failed to initiate bulk download.');
        });
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postUserContext, targetOrigin, addTask]);

  return (
    <CustomerMainLayout selected="31" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="Review Images"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>

      <AIEditorModalViewer
        ai_page={true}
        from="review"
        isOpen={isModalOpen}
        onClose={handleModalClose}
        imageData={selectedImageData}
        compareSiloModalOpen={compareSiloModalOpen}
        setCompareSiloModalOpen={setCompareSiloModalOpen}
        imageDisplayLoader={imageDisplayLoader}
        goToNextImage={goToNextImage}
        goToPreviousImage={goToPreviousImage}
        markFavorite={markAiImageFavorite}
        unmarkFavorite={unmarkAiImageFavorite}
        updatedRenderNameInImagesList={updatedRenderNameInImagesList}
        renderDelete={renderDelete}
        handleSubmitForReviewImageLibrary={handleSubmitForReviewImageLibrary}
        handleCollaborateAiImage={handleCollaborateAiImage}
        onRefresh={refetch}
        onAccept={canEditStatus ? handleAcceptFromModal : undefined}
        onReject={canEditStatus ? handleRejectFromModal : undefined}
      />

      {/* Standalone Compare Modal */}
      {!isModalOpen && (
        <CompareSiloView
          isOpen={compareSiloModalOpen}
          onClose={closeCompareModal}
          closeAllModals={closeCompareModal}
          imageData={selectedImageData}
        />
      )}
    </CustomerMainLayout>
  );
};

export default MigratedReviewImages;
